import { getCurrentLanguage } from '@/lib/languages';
import { getBranchCodeFromPath, isValidBranchCode } from '@/lib/config/branches-config';
import { localizePath } from '@/lib/config/utils';

const LEAD_GOAL_SESSION_PREFIX = 'lead-thank-you-goal:';
const PENDING_LEAD_SUBMISSION_PREFIX = 'pending-lead-submission:';

function normalizePathname(pathname?: string | null) {
  if (!pathname) {
    return '/';
  }

  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

export function getBranchCodeFromLocalizedPath(pathname?: string | null) {
  const normalizedPathname = normalizePathname(pathname);
  const localizedGeoMatch = normalizedPathname.match(/^\/(?:[a-z]{2}\/)?geo\/([^/]+)/i);
  const geoCode = localizedGeoMatch?.[1];

  if (geoCode && isValidBranchCode(geoCode)) {
    return geoCode;
  }

  return getBranchCodeFromPath(normalizedPathname);
}

export function buildLeadThankYouPath(pathname?: string | null) {
  const normalizedPathname = normalizePathname(pathname);
  const langCode = getCurrentLanguage(normalizedPathname).code;

  return localizePath('/spasibo', langCode);
}

export function buildLeadThankYouUrl({
  pathname,
  search,
  formType,
  leadId,
  sid,
}: {
  pathname?: string | null;
  search?: string | null;
  formType: string;
  leadId?: string | number | null;
  sid: string;
}) {
  const thankYouPath = buildLeadThankYouPath(pathname);
  const params = new URLSearchParams();
  const normalizedPathname = normalizePathname(pathname);
  const normalizedSearch = search && search.startsWith('?') ? search : search ? `?${search}` : '';

  params.set('from', `${normalizedPathname}${normalizedSearch}`);
  params.set('formType', formType);
  params.set('sid', sid);

  if (leadId !== undefined && leadId !== null && String(leadId).trim() !== '') {
    params.set('leadId', String(leadId).trim());
  }

  return `${thankYouPath}?${params.toString()}`;
}

export function buildOrderThankYouUrl({
  pathname,
  search,
  orderId,
}: {
  pathname?: string | null;
  search?: string | null;
  orderId?: string | number | null;
}) {
  const thankYouPath = buildLeadThankYouPath(pathname);
  const params = new URLSearchParams();
  const normalizedPathname = normalizePathname(pathname);
  const normalizedSearch = search && search.startsWith('?') ? search : search ? `?${search}` : '';

  params.set('from', `${normalizedPathname}${normalizedSearch}`);
  params.set('kind', 'order');

  if (orderId !== undefined && orderId !== null && String(orderId).trim() !== '') {
    params.set('orderId', String(orderId).trim());
  }

  return `${thankYouPath}?${params.toString()}`;
}

export function createLeadSubmissionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `lead-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getPendingLeadSubmissionStorageKey(formType: string, pathname?: string | null) {
  return `${PENDING_LEAD_SUBMISSION_PREFIX}${formType}:${normalizePathname(pathname)}`;
}

export function getOrCreatePendingLeadSubmissionId(formType: string, pathname?: string | null) {
  if (typeof window === 'undefined') {
    return createLeadSubmissionId();
  }

  const key = getPendingLeadSubmissionStorageKey(formType, pathname ?? window.location.pathname);
  const existing = window.sessionStorage.getItem(key)?.trim();
  if (existing) {
    return existing;
  }

  const submissionId = createLeadSubmissionId();
  window.sessionStorage.setItem(key, submissionId);
  return submissionId;
}

export function clearPendingLeadSubmissionId(formType: string, pathname?: string | null) {
  if (typeof window === 'undefined') {
    return;
  }

  const key = getPendingLeadSubmissionStorageKey(formType, pathname ?? window.location.pathname);
  window.sessionStorage.removeItem(key);
}

export function extractOptionalLeadId(payload: Record<string, unknown> | null | undefined) {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const leadId = payload.leadId ?? payload.elementId ?? payload.id;
  if (leadId === undefined || leadId === null) {
    return null;
  }

  const normalizedLeadId = String(leadId).trim();
  return normalizedLeadId || null;
}

export function getLeadGoalSessionStorageKey(submissionId: string) {
  return `${LEAD_GOAL_SESSION_PREFIX}${submissionId}`;
}
